import { useState } from "react";
import { toast } from "sonner";
import { Lock, Trash2, Ban } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";

export function ConnectionSettings({ open, onClose, conn, onChanged, onRemoved }) {
  const [perms, setPerms] = useState(conn?.my_shared_permissions || {});
  const [saving, setSaving] = useState(false);
  const labels = conn?.category_labels || {};
  const sensitive = conn?.sensitive_categories || [];
  const order = ["basic", "favorites", "likes_dislikes", "gift", "important", "about", "wishlist"];

  const toggle = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));

  const savePerms = async () => {
    setSaving(true);
    try {
      await api.put(`/connections/${conn.id}/permissions`, { permissions: perms });
      toast.success("Privacy updated");
      onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm("Remove this connection? The shared space will be archived and can be restored later.")) return;
    try { await api.post(`/connections/${conn.id}/archive`); onRemoved?.(); } catch (e) { toast.error(apiErr(e)); }
  };
  const block = async () => {
    if (!confirm("Block this person? They won't be able to send you requests, and access ends immediately.")) return;
    try { await api.post(`/connections/${conn.id}/block`); onRemoved?.(); } catch (e) { toast.error(apiErr(e)); }
  };

  if (!conn) return null;

  return (
    <Modal open={open} onClose={onClose} title="Connection settings" testId="connection-settings-modal">
      <div className="flex items-center gap-2 mb-2">
        <Lock size={15} className="text-sky-400" />
        <p className="text-sm font-medium text-slate-200">What can {conn.person.name} see of your profile?</p>
      </div>
      <p className="text-xs text-slate-500 mb-4">Everything is private by default. Toggle on only what you want to share. This is enforced on the server.</p>

      <div className="space-y-1.5 mb-5">
        {order.map((k) => (
          <label key={k} className="flex items-center justify-between iks-card px-4 py-3 cursor-pointer" data-testid={`perm-${k}`}>
            <span className="text-sm text-slate-200 flex items-center gap-2">
              {labels[k] || k}
              {sensitive.includes(k) && <span className="text-[10px] text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded-full">Sensitive</span>}
            </span>
            <button
              type="button"
              onClick={() => toggle(k)}
              data-testid={`perm-toggle-${k}`}
              className={`relative w-11 h-6 rounded-full transition-colors ${perms[k] ? "bg-blue-600" : "bg-slate-700"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${perms[k] ? "translate-x-5" : ""}`} />
            </button>
          </label>
        ))}
      </div>
      <Button full loading={saving} onClick={savePerms} data-testid="save-permissions-btn">Save privacy</Button>

      <div className="border-t border-white/10 mt-6 pt-5 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Danger zone</p>
        <Button variant="destructive" full onClick={remove} data-testid="remove-connection-btn"><Trash2 size={16} /> Remove connection</Button>
        <Button variant="destructive" full onClick={block} data-testid="block-connection-btn"><Ban size={16} /> Block person</Button>
      </div>
    </Modal>
  );
}
